import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../lib/auth';
import { useAddProvider } from '../api/hooks';
import { Stethoscope } from 'lucide-react';
import { toast } from '../lib/aiToast';

// Add a rendering provider (practitioner) to the practice. The NPI signs the 270
// on eligibility checks, so it's the key field. onCreated(provider) lets callers
// (e.g. the Run-check form) auto-select the newly added provider.
export default function AddProvider({ open, onClose, onCreated, practiceId }) {
  const { session } = useAuth();
  const pid = practiceId || session?.practice?.id || 'prac_001';
  const add = useAddProvider();

  const [f, setF] = useState({ firstName: '', lastName: '', npi: '', taxonomyCode: '', licenseState: '' });
  useEffect(() => {
    if (open) setF({ firstName: '', lastName: '', npi: '', taxonomyCode: '', licenseState: '' });
  }, [open]);

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  // NPI is a 10-digit identifier; empty is allowed (added later), else require 10 digits.
  const npiValid = !f.npi || /^\d{10}$/.test(f.npi);
  const canSubmit = f.firstName.trim() && f.lastName.trim() && npiValid && !add.isPending;

  const submit = async () => {
    if (!canSubmit) return;
    try {
      const provider = await add.mutateAsync({
        practiceId: pid,
        firstName: f.firstName.trim(),
        lastName: f.lastName.trim(),
        npi: f.npi.trim() || undefined,
        taxonomyCode: f.taxonomyCode.trim() || undefined,
        licenseState: f.licenseState.trim() || undefined,
      });
      toast.success(`Added Dr. ${provider.firstName} ${provider.lastName}`);
      onCreated?.(provider);
      onClose?.();
    } catch (e) {
      toast.error(e?.userMessage || 'Could not add provider');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Stethoscope className="h-4 w-4 text-brand-600" />Add provider</DialogTitle>
          <DialogDescription>Add a rendering practitioner. The NPI is used to sign 270 eligibility requests.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3.5 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label htmlFor="pv-fn">First name</Label><Input id="pv-fn" value={f.firstName} onChange={set('firstName')} placeholder="Elena" /></div>
            <div className="grid gap-1.5"><Label htmlFor="pv-ln">Last name</Label><Input id="pv-ln" value={f.lastName} onChange={set('lastName')} placeholder="Martinez" /></div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="pv-npi">NPI <span className="text-slate-400 font-normal">(10 digits, optional)</span></Label>
            <Input id="pv-npi" value={f.npi} onChange={set('npi')} maxLength={10} placeholder="1942758109" className={!npiValid ? 'border-rose-400' : ''} />
            {!npiValid && <span className="text-xs text-rose-500">NPI must be exactly 10 digits.</span>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label htmlFor="pv-tax">Taxonomy <span className="text-slate-400 font-normal">(optional)</span></Label><Input id="pv-tax" value={f.taxonomyCode} onChange={set('taxonomyCode')} placeholder="207N00000X" /></div>
            <div className="grid gap-1.5"><Label htmlFor="pv-st">License state <span className="text-slate-400 font-normal">(optional)</span></Label><Input id="pv-st" value={f.licenseState} onChange={set('licenseState')} maxLength={2} placeholder="CA" /></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose?.()}>Cancel</Button>
          <Button className="bg-brand-600 hover:bg-brand-700 text-white" disabled={!canSubmit} onClick={submit}>Add provider</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
